import { getImage } from 'gatsby-plugin-image'
import React from 'react'
import styled from 'styled-components'
import GetFocalRatio from '../../helpers/getFocalRatio'
import Parallax from '../../helpers/parallax'
import renderDocumentToReactComponents from '../../modules/contentful/renderDocumentToReactComponents/renderDocumentToReactComponents'
import ArrowLink from '../atoms/arrowLink'
import {
  HeroImage,
  StyledMetaInformationContainer,
} from '../atoms/style-helpers'
import theme from '../atoms/theme'

type CenteredPageHeaderProps = {
  block: Queries.ContentfulHeroPageHeaderBlock
}

const { colours, breakpoints } = theme

export const StyledContainer = styled.div`
  @media only screen and ${breakpoints.fromNormalScreen} {
    margin-top: -5.5rem;
  }
`

export const LightMetaInformation = styled.div`
  background-color: ${colours.light};
  color: ${colours.dark};
  display: flex;
  padding: 2.6rem 0;
  flex-basis: 75%;

  @media only screen and ${breakpoints.toNormalScreen} {
    flex-direction: column;
    padding: 2.6rem 0 0 0;
  }
`

export const StyledMetaInformation = styled(LightMetaInformation)`
  position: relative;
  flex-basis: ${(props): string | null => (props.fullWidth ? '100%' : '75%')};

  @media only screen and ${breakpoints.toNormalScreen} {
    padding: 0;
  }

  @media only screen and ${breakpoints.fromNormalScreen} {
    padding-right: 2.4rem;
    padding-left: 2.4rem;
  }
`

export const LightMetaSection = styled.div`
  flex-basis: 100%;
  h4 {
    font-size: 1.8rem;
  }
  a {
    margin-top: 2.2rem;
  }

  @media only screen and ${breakpoints.toNormalScreen} {
    flex-direction: column;
    padding: 1.8rem 2rem;
    text-align: left;
    h4 {
      margin-bottom: 0;
    }
    a {
      display: flex;
      justify-content: center;
      width: 100%;
    }
  }

  @media only screen and ${breakpoints.fromNormalScreen} {
    margin-right: 2rem;
  }

  p {
    margin: 0;
  }
`

export const StyledLinkContainer = styled.div`
  margin-top: 1.6rem;
  a {
    margin-top: unset;
    display: inline;
  }
`

const CenteredPageHeader = (props: CenteredPageHeaderProps): JSX.Element => {
  const {
    heroImage,
    firstColContent,
    firstColLink,
    firstColLinkUrl,
    secondColContent,
    secondColLink,
    secondColLinkUrl,
    thirdColContent,
    thirdColLink,
    thirdColLinkUrl,
  } = props.block
  const imageData =
    heroImage?.image && getImage(heroImage.image?.gatsbyImageData)
  return (
    <>
      <HeroImage>
        <Parallax
          image={imageData}
          focalRatio={heroImage && GetFocalRatio(heroImage)}
        />
      </HeroImage>
      <StyledContainer>
        <StyledMetaInformationContainer>
          <StyledMetaInformation fullWidth>
            <LightMetaSection>
              {firstColContent?.raw &&
                renderDocumentToReactComponents(
                  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
                  firstColContent as { raw: string; references: any[] },
                )}

              <StyledLinkContainer>
                {firstColLinkUrl && (
                  <ArrowLink to={firstColLinkUrl}>
                    <span>{firstColLink}</span>
                  </ArrowLink>
                )}
              </StyledLinkContainer>
            </LightMetaSection>

            <LightMetaSection>
              {secondColContent?.raw &&
                renderDocumentToReactComponents(
                  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
                  secondColContent as { raw: string; references: any[] },
                )}
              <StyledLinkContainer>
                {secondColLinkUrl && (
                  <ArrowLink to={secondColLinkUrl}>
                    <span>{secondColLink}</span>
                  </ArrowLink>
                )}
              </StyledLinkContainer>
            </LightMetaSection>

            <LightMetaSection>
              {thirdColContent?.raw &&
                renderDocumentToReactComponents(
                  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
                  thirdColContent as { raw: string; references: any[] },
                )}
              <StyledLinkContainer>
                {thirdColLinkUrl && (
                  <ArrowLink to={thirdColLinkUrl}>
                    <span>{thirdColLink}</span>
                  </ArrowLink>
                )}
              </StyledLinkContainer>
            </LightMetaSection>
          </StyledMetaInformation>
        </StyledMetaInformationContainer>
      </StyledContainer>
    </>
  )
}

export default CenteredPageHeader
